import React, { useEffect, useMemo, useState } from "react";
import SectionField from "../section/SectionField";
import styles from "./StopWatch.module.scss";
import InputTextArea from "components/shared/textArea/InputTextArea";
import FileUploadButton from "components/shared/fileUploadButton/FileUploadButton";
import { useForm, Controller } from "react-hook-form";
import TriggerButton from "components/shared/createTriggerButton/TriggerButton";
import { setDroppedItems, setUpdateDroppedItems } from "redux/slices/formSlice";
import { useDispatch, useSelector } from "react-redux";
import { handleFile, randomIntFromInterval } from "utils/utils";
import DeleteIcon from "@mui/icons-material/Delete";
import { setPublish } from "redux/slices/globalSlice";
import { setDroppedItems as setTriggerList } from "redux/slices/triggerSlice";
import { useNavigate, useParams } from "react-router-dom";
import { MenuItem, Select } from "@mui/material";
import ScoreRangeComponent from "components/scorerange/ScoreRangeComponent";
import toast from "react-hot-toast";

const stopWatchOptions = [
  "Pass",
  "Fail",
  "Pass or Fail",
  "Neutral",
]
const StopWatch = () => {
  const { publish } = useSelector((state) => state?.global);
  const dispatch = useDispatch();
  const [enablePassFail, setEnablePassFail] = useState(false)
  const navigate = useNavigate()
  const [dependent, setDependent] = useState(false)
  const [dependentOptions, setDependentOptions] = useState(stopWatchOptions[0])
  const { triggerDroppedList } = useSelector((state) => state.trigger);
  const { droppedItems, selectedData } = useSelector((state) => state.form);
  const { formId } = useParams()
  const [file, setFile] = useState("");

  // Use `useForm` to set default values and track form inputs
  const { register, control, formState: { isDirty }, setValue, watch } = useForm({
    defaultValues: {
      showInstructions: false,
      typeOfStopwatch: selectedData.typeOfStopwatch ? "stopwatch" : "manual", // Set initial value based on `selectedData`
      manual: false,
      allowNA: false,
      answers: [
        { label: "Yes", evaluation: "" },
        { label: "No", evaluation: "" },
      ],
      instructions: selectedData?.instructions || "",
      enableDependentQuestions: selectedData?.enableDependentQuestions || false,
    },
  });

  const currentItem = useMemo(
    () => droppedItems.find((item) => item.id === selectedData.id),
    [droppedItems, selectedData]
  );

  const handleChange = (field, value) => {
    const updatedData = { [field]: value };
    if (field == "enableDependentQuestions") {
      setDependent(updatedData?.enableDependentQuestions)
    }
    else if (field == "whenEvaluationEquals") {
      setDependent(true)
    }
    else if (field == "enablePassFail") {
      setEnablePassFail(value)
    }
  };


  // Handle file input change
  const handleFileChange = (file, setIsLoading) => {
    setFile(file);
    handleFile(file, droppedItems, selectedData, dispatch, setDroppedItems, setIsLoading);;
    dispatch(setPublish(true));
  };



  // Handle radio button changes for "StopWatch" and "manual"
  const handleTimerTypeChange = (type) => {
    if (type === "stopwatch") {
      handleChange("manual", false);
      setValue("typeOfStopwatch", "stopwatch"); // Sync with react-hook-form
    } else {
      handleChange("manual", true);
      setValue("typeOfStopwatch", "manual"); // Sync with react-hook-form
    }
  };

  const handleImageDelete = (src, selectedItem) => {
    const updatedMedia = selectedItem?.data?.media.filter(
      (media) => media.path !== src
    );

    const updatedData = { media: updatedMedia };

    dispatch(setUpdateDroppedItems({ id: selectedData.id, updatedData }));
    setFile("");
    dispatch(setPublish(true));
  };

  const handleOpenImage = (src) => {
    window.open(src, "__blank");
  };


  const handleTrigger = () => {
    const uniqueId = Date.now() + randomIntFromInterval(1000, 9999);
    dispatch(setTriggerList([
      ...triggerDroppedList, {
        ...currentItem,
        id: uniqueId
      }
    ]))
    navigate(`/create/triggers/${formId || ""}`)


  }
  const handleChangeSelect = (e) => {
    setDependentOptions(e?.target?.value)
    handleChange(e?.target?.name, e?.target?.value)
  }
  const handleRangeSet = (val) => {
    handleChange("whenAnswerOutsideRange", val)
  }

  useEffect(() => {
    // Sync the selectedData.stopWatch with the form field value
    setValue("timerType", selectedData.stopWatch ? "stopwatch" : "manual");
  }, [selectedData.stopWatch, setValue]);


  useEffect(() => {
    if (!isDirty) {
      const data = structuredClone(watch())
      dispatch(setUpdateDroppedItems({ id: selectedData.id, updatedData: data }));
    }

    const subscription = watch((data) => {
      const formData = structuredClone(data)
      dispatch(setUpdateDroppedItems({ id: selectedData.id, updatedData: formData }));
      dispatch(setPublish(true))
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <>
      <SectionField
        placeholder="Please enter your question here."
        message="Required"
      />
      <div className={styles.radioContainer}>
        <label className={styles.stopWatch}>
          <input
            type="radio"
            name="typeOfStopwatch"
            value="stopwatch"
            checked={watch("typeOfStopwatch") === "stopwatch"} // Watch the value to ensure correct UI reflection
            {...register("typeOfStopwatch")}
            onChange={() => handleTimerTypeChange("stopwatch")}
          />
          Stop Watch
        </label>
        <label className={styles.manualEntry}>
          <input
            type="radio"
            name="typeOfStopwatch"
            value="manual"
            checked={watch("typeOfStopwatch") === "manual"} // Watch the value to ensure correct UI reflection
            {...register("typeOfStopwatch")}
            onChange={() => handleTimerTypeChange("manual")}
          />
          Manual Entry
        </label>
      </div>
      <div className={styles?.instructionsForm}>
        <h4>Instructions</h4>
        <div className={styles?.formField}>
          <Controller
            name="instructions"
            control={control}
            render={({ field }) => (
              <InputTextArea
                {...field}
                control={control}
                type="textarea"
                placeholder="Optional"
                onChange={(e) => {
                  field.onChange(e);
                  if(!e.target.value) setValue("showInstructions", false);
                }}
                className={styles.textArea}
              />
            )}
          />
        </div>
        <div className={styles?.formField}>
          {currentItem?.data?.media?.length ? (
            <div className={styles.imageContainer}>
              {currentItem?.data?.media?.map((src, index) => (
                <div
                  className={styles.imageWrapper}
                  onClick={() => handleOpenImage(src.path)}
                >
                  <img src={src.path} alt="" />
                  <div
                    className={styles.cancelIcon}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleImageDelete(src.path, currentItem);
                    }}
                  >
                    <DeleteIcon sx={{ width: "20px", height: "20px" }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <FileUploadButton
              name="mediaFile"
              control={control}
              onFileChange={handleFileChange}
            />
          )}
        </div>
      </div>

      <form className={styles.formContainer}>
        <div>
          <label>
            <input
              type="checkbox"
              disabled={
                !watch("instructions")  && !file && !currentItem?.data?.media?.length > 0
              }
              {...register("showInstructions")}
              />
            Show instructions by default
          </label>
        </div>
        <div >
          <label>
            <input
              type="checkbox"
              {...register("enablePassFail")}
              onChange={(e) => {
                if (!e.target.checked) {
                  if (window.confirm("Un-checking this option will remove all dependent questions and any data collected from those questions. Are you sure?")) {
                    handleChange("enablePassFail", e.target.checked)
                  } else {
                    return
                  }
                }
                handleChange("enablePassFail", e.target.checked)
              }
              }
            />
            Enable Pass/Fail Range
          </label>
          {
            enablePassFail && <ScoreRangeComponent type="form" handleRangeSet={handleRangeSet} handleChange={handleChange} control={control} />
          }
        </div>
        <div>
          <label>
            <input
              disabled={!enablePassFail}
              type="checkbox"
              {...register("enableDependentQuestions")}
            />
            Enable Dependent Questions
          </label>
          {
            dependent ? <>
              <label style={{ margin: "10px 0px" }}>Display when answer evaluates to:</label>
              <Select onChange={handleChangeSelect} name="whenEvaluationEquals" value={dependentOptions} sx={{ width: "100%", height: "40px" }}>
                {
                  stopWatchOptions?.map((option) => {
                    return <MenuItem value={option}>{option}</MenuItem>
                  })
                }
              </Select>

            </> : null

          }
        </div>
        <TriggerButton handleClick={() => {
          if (!publish) {
            handleTrigger()
          } else {
            toast.error("There are unpublished changes");
          }
        }
        } />
      </form>
    </>
  );
};

export default StopWatch;
